import { AxiosResponse } from "axios";
import {
  ApplicantDto,
  ApplicantsToInviteDto,
  ApplicationDto,
  ApplicationImageDto,
  ApplicationPayloadDto,
  ApplicationPhaseDto,
  ChangeRatingDto,
  FeedbackStatisticsDto,
  GenerationDto,
  NewGenerationRequestDto,
  TraineeEvaluationDto,
} from "../types/applicationTypes";
import api from "../utils/api";
import { combinePhoneNumber } from "../utils/phoneUtils";

/**
 * Save the application
 * @param application The application to save
 */
export const saveApplication = async (
  application: ApplicationDto,
  applicationImage: ApplicationImageDto
): Promise<boolean> => {
  const { mobileCountryCode, mobilePhone, ...restApplication } = application;
  const payload: ApplicationPayloadDto = {
    ...restApplication,
    mobilePhone: combinePhoneNumber(mobileCountryCode, mobilePhone),
  };
  const data = { application: payload, applicationImage: applicationImage };
  // Make API call to save the application
  return await api.post(`/application`, data);
};

/**
 * Get all evaluations for a member
 * @returns TraineeEvaluationDto[]
 */
export const getApplicantsEvaluations = async (): Promise<AxiosResponse<TraineeEvaluationDto[]>> => {
  return await api.get<TraineeEvaluationDto[]>(`/application/evaluations`);
};

/**
 * Get the feedback statistics
 * @returns FeedbackStatisticsDto
 */
export const getFeedbackStatistics = async (): Promise<AxiosResponse<FeedbackStatisticsDto>> => {
  return await api.get<FeedbackStatisticsDto>(`/application/feedback`);
};

/**
 * Get the current generation
 * @returns GenerationDto
 */
export const getCurrentGeneration = async (): Promise<AxiosResponse<GenerationDto>> => {
  return await api.get<GenerationDto>(`/application/generation`);
};

/**
 * Get the application phase
 * @returns ApplicationPhaseDto
 */
export const getApplicationPhase = async (): Promise<AxiosResponse<ApplicationPhaseDto>> => {
  return await api.get<ApplicationPhaseDto>(`/application/application-phase`);
};

/**
 * Create a new generation
 * @returns GenerationDto
 */
export const createNewGeneration = async (
  newGeneration: NewGenerationRequestDto
): Promise<AxiosResponse<GenerationDto>> => {
  return await api.post<GenerationDto>(`/application/generation`, newGeneration);
};

/**
 * Update a generation
 * @returns GenerationDto
 */
export const updateGeneration = async (generation: GenerationDto): Promise<AxiosResponse<GenerationDto>> => {
  return await api.patch<GenerationDto>(`/application/generation`, generation);
};

/**
 * Update the evaluation of a trainee
 * @param evaluation The evaluation to update
 * @returns TraineeEvaluationDto
 */
export const updateTraineeEvaluation = async (
  evaluation: ChangeRatingDto
): Promise<AxiosResponse<TraineeEvaluationDto>> => {
  return await api.post<TraineeEvaluationDto>(`/application/evaluations/${evaluation.traineeApplicantId}`, evaluation);
};

/**
 * Delete an application
 * @param applicationId The id of the application to delete
 * @returns boolean
 */
export const deleteApplication = async (applicationId: number): Promise<boolean> => {
  return await api.delete(`/application/${applicationId}`);
};

/**
 * Invite applicants to the selection weekend
 * @param applicantsToInvite The applicants to invite
 * @returns boolean
 */
export const inviteApplicants = async (applicantsToInvite: ApplicantsToInviteDto): Promise<boolean> => {
  return await api.post(`/application/invitation`, applicantsToInvite);
};

/**
 * Admit applicants to the selection weekend
 * @param applicantsToAdmit The applicants to admit
 * @returns boolean
 */
export const admitApplicants = async (applicantsToAdmit: ApplicantsToInviteDto): Promise<boolean> => {
  return await api.post(`/application/admission`, applicantsToAdmit);
};

/**
 * Get the details of an applicant
 * @param applicantId The id of the applicant to reject
 * @returns the details of the applicant
 */
export const getApplicantDetails = async (applicantId: number): Promise<AxiosResponse<ApplicantDto>> => {
  return await api.get<ApplicantDto>(`/application/${applicantId}`);
};

/**
 * Get the image of an applicant
 * @param applicantId The id of the applicant
 * @returns the image of the applicant
 */
export const getApplicantImage = async (applicantId: number): Promise<AxiosResponse<ApplicationImageDto>> => {
  return await api.get<ApplicationImageDto>(`/application/${applicantId}/image`);
};
